import { Marker, Popup } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import { useEffect, useRef } from "react";
import { MockTractorLocation } from "@/lib/mock-tractor-locations";
import { Navigation, Clock, MapPin, WifiOff, Container, FileText } from "lucide-react";
import { formatRouteDirection } from "@/lib/heading-utils";

// Capability-based colors (raw CSS — no Tailwind in divIcon HTML)
const CAPABILITY_COLORS: Record<string, string> = {
  VO:     "#3b82f6", // blue-500
  NVO:    "#f59e0b", // amber-500
  Manual: "#8b5cf6", // violet-500
};

function getSpeedHex(speed: number): string {
  if (speed >= 50) return "#22c55e";
  if (speed >= 10) return "#3b82f6";
  return "#6b7280";
}

function getPrimaryCapabilityColor(capabilities?: string[]): string {
  if (!capabilities || capabilities.length === 0) return CAPABILITY_COLORS.Manual;
  for (const cap of ["VO", "NVO", "Manual"]) {
    if (capabilities.includes(cap)) return CAPABILITY_COLORS[cap];
  }
  return "#6b7280"; // gray fallback
}

function createTruckIcon(capabilities?: string[], dimmed = false) {
  const color = dimmed ? "#9ca3af" : getPrimaryCapabilityColor(capabilities);
  const opacity = dimmed ? 0.35 : 1;
  return L.divIcon({
    className: "custom-truck-marker",
    html: `<div style="
      width: 28px; height: 28px; border-radius: 50%;
      background: ${color}; border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      opacity: ${opacity};
    ">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
        <path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.949V8a1 1 0 0 1 1-1h1.382a1 1 0 0 1 .894.553l1.448 2.894A1 1 0 0 0 19.382 11H20a2 2 0 0 1 2 2v4a1 1 0 0 1-1 1h-1"/>
        <circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>
      </svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -18],
  });
}


interface TractorMapMarkerProps {
  tractor: MockTractorLocation & {
    capabilities?: string[];
    trailerId?: string | null;
    orderNumber?: string | null;
    orderId?: string | null;
  };
  dimmed?: boolean;
  isStale?: boolean;
  forceOpen?: boolean;
}

export function TractorMapMarker({ tractor, dimmed = false, isStale = false, forceOpen = false }: TractorMapMarkerProps) {
  const markerDimmed = dimmed || isStale;
  const markerRef = useRef<L.Marker>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    if (forceOpen) {
      marker.openPopup();
    } else {
      marker.closePopup();
    }
  }, [forceOpen]);

  return (
    <Marker
      ref={markerRef}
      position={[tractor.lat, tractor.lng]}
      icon={createTruckIcon(tractor.capabilities, markerDimmed)}
    >
      <Popup className="tractor-popup" minWidth={220} maxWidth={280}>
        <div className="space-y-2 p-1">
          <div className="flex items-center justify-between">
            <span className="font-bold text-sm">{tractor.name}</span>
          </div>

          {(tractor.trailerId || tractor.orderNumber) && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {tractor.trailerId && (
                <span className="flex items-center gap-1 truncate">
                  <Container className="h-3 w-3 shrink-0" />
                  {tractor.trailerId}
                </span>
              )}
              {tractor.orderNumber && (
                <span className="flex items-center gap-1 truncate">
                  <FileText className="h-3 w-3 shrink-0" />
                  {tractor.orderId ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/orders/${tractor.orderId}`)}
                      className="truncate hover:text-foreground hover:underline"
                    >
                      {tractor.orderNumber}
                    </button>
                  ) : (
                    tractor.orderNumber
                  )}
                </span>
              )}
            </div>
          )}

          <div className="space-y-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              <span>{tractor.currentCity}</span>
            </div>

            {tractor.speed > 0 && (
              <div className="flex items-center gap-1.5" style={{ color: getSpeedHex(tractor.speed) }}>
                <Navigation
                  className="h-3 w-3"
                  style={
                    tractor.headingDeg != null
                      ? { transform: `rotate(${tractor.headingDeg}deg)` }
                      : undefined
                  }
                />
                <span>
                  {tractor.speed} mph · {tractor.heading}
                  {(() => {
                    const route = formatRouteDirection(
                      tractor.currentCity,
                      tractor.headingDeg,
                      tractor.heading
                    );
                    return route ? ` · ${route}` : "";
                  })()}
                </span>
              </div>
            )}

            {tractor.nextStop && (
              <div className="flex items-center gap-1.5">
                <span className="font-medium">Next:</span>
                <span>{tractor.nextStop}</span>
              </div>
            )}

            <div className={`flex items-center gap-1.5 ${isStale ? "text-amber-500" : ""}`}>
              {isStale ? <WifiOff className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              <span>
                {isStale
                  ? `Last seen ${tractor.lastUpdate} — not reporting live`
                  : `Updated ${tractor.lastUpdate}`}
              </span>
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}
